import { energyKwh } from "../../lib/publicOrder";
import type { FlowKind, MoneyFlow } from "../../lib/orderFlow";

/**
 * Where an order's money went, as one bar and its legend.
 *
 * The legend is the reading; the bar is the proportion. Each segment is named in
 * words with its amount, so the colours are a shortcut and never the only
 * channel — the same rule `StatusMark` follows.
 */

const FILL: Readonly<Record<FlowKind, string>> = {
  used: "bg-ink",
  refunded: "bg-state-available",
  /** The one segment that needs somebody, so it takes the signal colour. */
  owed: "bg-signal",
  kept: "bg-state-idle",
  released: "bg-rule",
  uncollected: "bg-signal",
  /** Not divided yet. Hatched, so it reads as pending rather than as a figure. */
  open: "bg-[repeating-linear-gradient(135deg,var(--color-state-transitional)_0_4px,transparent_4px_8px)]",
};

const NAME: Readonly<Record<FlowKind, string>> = {
  used: "used",
  refunded: "refunded",
  owed: "owed back",
  kept: "kept, under the refund floor",
  released: "released to the card",
  uncollected: "delivered, never collected",
  open: "not divided until the charge closes",
};

export function MoneyFlowBar({
  flow,
  deliveredWh,
}: {
  readonly flow: MoneyFlow;
  readonly deliveredWh: string | null;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div
        aria-hidden="true"
        className="flex h-3 w-full gap-px overflow-hidden rounded-hairline bg-paper-sunken"
      >
        {flow.segments.map((part) => (
          <span
            key={part.kind}
            className={`h-full ${FILL[part.kind]}`}
            style={{ width: `${part.share}%`, minWidth: "3px" }}
          />
        ))}
      </div>
      <ul className="m-0 flex list-none flex-wrap gap-x-6 gap-y-2 p-0">
        {flow.segments.map((part) => (
          <li key={part.kind} className="flex items-center gap-2 text-data">
            <span aria-hidden="true" className={`block size-2 shrink-0 ${FILL[part.kind]}`} />
            <span
              className={`font-semibold tabular-nums ${part.kind === "owed" || part.kind === "uncollected" ? "text-signal" : "text-ink"}`}
            >
              {part.amount}
            </span>
            <span className="text-ink-muted">
              {NAME[part.kind]}
              {/* The energy the used money bought, beside it: the one figure that
                  makes the split checkable against the price. */}
              {(part.kind === "used" || part.kind === "uncollected") && deliveredWh !== null
                ? ` · ${energyKwh(deliveredWh)} kWh`
                : ""}
            </span>
          </li>
        ))}
      </ul>
      {flow.overRefunded === undefined ? null : (
        <p className="m-0 text-data text-signal">
          {flow.overRefunded} more was refunded than the charge left unused — likely a hand refund
          in the merchant portal. Check it against the gateway.
        </p>
      )}
    </div>
  );
}
